import * as React from "react";
import { ownerDocument } from "../../../utils/owner.js";
import { useIsoLayoutEffect } from "../../../utils/useIsoLayoutEffect.js";
import { useStableCallback } from "../../../utils/useStableCallback.js";
import { useBaseUiId } from "../../internals/useBaseUiId.js";
import { useRenderElement } from "../../internals/useRenderElement.js";
import { ACTIVE_COMPOSITE_ITEM } from "../../internals/composite/constants.js";
import { useCompositeItem } from "../../internals/composite/item/useCompositeItem.js";
import { useCompositeRootContext } from "../../internals/composite/root/CompositeRootContext.js";
import { useTabsRootContext } from "../root/TabsRootContext.js";
import { tabsStateAttributesMapping } from "../root/stateAttributesMapping.js";
import { useTabsListContext } from "../list/TabsListContext.js";
import { createChangeEventDetails } from "../../internals/createBaseUIEventDetails.js";
import { activeElement, contains } from "../../../utils/shadowDom.js";
import { useButton } from "../../internals/use-button/useButton.js";
import { none } from "../../internals/reason-parts.js";
const TabsTab = /* @__PURE__ */ React.forwardRef(function TabsTab2(componentProps, forwardedRef) {
  const {
    className,
    disabled = false,
    render,
    value,
    id: idProp,
    nativeButton = true,
    style,
    ...elementProps
  } = componentProps;
  const {
    value: activeTabValue,
    getTabPanelIdByValue,
    onValueChange,
    orientation,
    tabActivationDirection
  } = useTabsRootContext();
  const {
    activateOnFocus,
    registerTabResizeObserverElement,
    tabsListElement
  } = useTabsListContext();
  const {
    highlightedIndex,
    onHighlightedIndexChange
  } = useCompositeRootContext();
  const id = useBaseUiId(idProp);
  const tabMetadata = React.useMemo(() => ({
    disabled,
    id,
    value
  }), [disabled, id, value]);
  const {
    compositeProps,
    compositeRef,
    index
    // hook is used instead of the CompositeItem component
    // because the index is needed for Tab internals
  } = useCompositeItem({
    metadata: tabMetadata
  });
  const active = value === activeTabValue;
  const isNavigatingRef = React.useRef(false);
  const unobserveTabElementRef = React.useRef(null);
  const observeTabElement = useStableCallback((element2) => {
    unobserveTabElementRef.current?.();
    unobserveTabElementRef.current = element2 ? registerTabResizeObserverElement(element2) : null;
  });
  useIsoLayoutEffect(() => {
    if (isNavigatingRef.current) {
      isNavigatingRef.current = false;
      return;
    }
    if (!(active && index > -1 && highlightedIndex !== index)) {
      return;
    }
    const listElement = tabsListElement;
    if (listElement != null) {
      const activeEl = activeElement(ownerDocument(listElement));
      if (activeEl && contains(listElement, activeEl)) {
        return;
      }
    }
    if (!disabled) {
      onHighlightedIndexChange(index);
    }
  }, [active, index, highlightedIndex, onHighlightedIndexChange, disabled, tabsListElement]);
  const {
    getButtonProps,
    buttonRef
  } = useButton({
    disabled,
    native: nativeButton,
    focusableWhenDisabled: true
  });
  const tabPanelId = getTabPanelIdByValue(value);
  const isPressingRef = React.useRef(false);
  const isMainButtonRef = React.useRef(false);
  function activate(event) {
    onValueChange(value, createChangeEventDetails(none, event.nativeEvent, void 0, {
      activationDirection: "none"
    }));
  }
  function onClick(event) {
    if (active || disabled) {
      return;
    }
    activate(event);
  }
  function onFocus(event) {
    if (active || disabled) {
      return;
    }
    if (activateOnFocus && (!isPressingRef.current || // keyboard or touch focus
    isMainButtonRef.current)) {
      activate(event);
    }
  }
  function onPointerDown(event) {
    if (active || disabled) {
      return;
    }
    isPressingRef.current = true;
    isMainButtonRef.current = event.button === 0;
    const doc = ownerDocument(event.currentTarget);
    function handlePointerEnd() {
      isPressingRef.current = false;
      isMainButtonRef.current = false;
      doc.removeEventListener("pointerup", handlePointerEnd);
      doc.removeEventListener("pointercancel", handlePointerEnd);
    }
    doc.addEventListener("pointerup", handlePointerEnd);
    doc.addEventListener("pointercancel", handlePointerEnd);
  }
  const state = {
    disabled,
    active,
    orientation,
    tabActivationDirection
  };
  const element = useRenderElement("button", componentProps, {
    state,
    ref: [forwardedRef, buttonRef, compositeRef, observeTabElement],
    props: [compositeProps, {
      role: "tab",
      "aria-controls": tabPanelId,
      "aria-selected": active,
      id,
      onClick,
      onFocus,
      onPointerDown,
      [ACTIVE_COMPOSITE_ITEM]: active ? "" : void 0,
      onKeyDownCapture() {
        isNavigatingRef.current = true;
      }
    }, elementProps, getButtonProps],
    stateAttributesMapping: tabsStateAttributesMapping
  });
  return element;
});
if (process.env.NODE_ENV !== "production") TabsTab.displayName = "TabsTab";
export {
  TabsTab
};
//# sourceMappingURL=TabsTab.js.map
